import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiClient } from "@/api/client";
import { queryKeys, type BosunProjectRead } from "@/api/queries";
import ErrorFallback from "@/components/ErrorFallback";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatError } from "@/lib/format-error";

export const Route = createFileRoute(
  "/_authenticated/projects/$workspaceUuid/$projectUuid/slack/oauth/callback",
)({
  component: ProjectSlackOAuthCallbackPage,
  errorComponent: ErrorFallback,
});

type CallbackState = "pending" | "success" | "error";

function readAndClearCode(): string | null {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  return code && code.trim() ? code : null;
}

export function ProjectSlackOAuthCallbackPage() {
  const { workspaceUuid, projectUuid } = Route.useParams();
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<CallbackState>("pending");
  const [error, setError] = React.useState<string | null>(null);
  const codeRef = React.useRef<string | null | undefined>(undefined);
  const didStartRef = React.useRef(false);
  if (codeRef.current === undefined) {
    codeRef.current = readAndClearCode();
  }

  React.useEffect(() => {
    if (didStartRef.current) return;
    didStartRef.current = true;

    async function completeOAuth() {
      const code = codeRef.current;
      if (!code) {
        setState("error");
        setError("Slack did not return an OAuth code. Open the Slack Allow URL again from provisioning.");
        return;
      }

      try {
        const { data, error: responseError } = await apiClient.POST(
          "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/slack/oauth-callback",
          {
            params: {
              path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
              query: { code },
            },
          },
        );
        if (responseError) throw new ApiError(responseError);
        queryClient.setQueryData<BosunProjectRead>(
          queryKeys.workspaces.bosunProjects.detail(workspaceUuid, projectUuid),
          data,
        );
        await queryClient.invalidateQueries({
          queryKey: queryKeys.workspaces.bosunProjects.detail(workspaceUuid, projectUuid),
        });
        setState("success");
      } catch (err) {
        setState("error");
        setError(formatError(err));
      }
    }

    void completeOAuth();
  }, [projectUuid, queryClient, workspaceUuid]);

  const provisioningLink = (
    <Button asChild variant={state === "error" ? "secondary" : "default"}>
      <Link
        to="/projects/$workspaceUuid/$projectUuid/provisioning"
        params={{ workspaceUuid, projectUuid }}
      >
        Return to provisioning
      </Link>
    </Button>
  );

  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-2xl items-center px-4 py-10 md:px-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {state === "pending" ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : null}
            {state === "success" ? <CheckCircle2 className="size-5 text-success" /> : null}
            {state === "error" ? <AlertTriangle className="size-5 text-destructive" /> : null}
            Slack app authorization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "pending" ? (
            <p className="text-sm text-muted-foreground">
              Completing the Slack app install for this Bosun project.
            </p>
          ) : null}
          {state === "success" ? (
            <>
              <Alert tone="success">
                <AlertDescription>
                  Slack authorization completed. Return to provisioning and retry the project setup so Bosun can bind channels.
                </AlertDescription>
              </Alert>
              {provisioningLink}
            </>
          ) : null}
          {state === "error" ? (
            <>
              <Alert tone="danger">
                <AlertDescription>
                  {error || "Slack authorization could not be completed."}
                </AlertDescription>
              </Alert>
              {provisioningLink}
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
